import { createContext, useContext } from 'react';
import { IntlContext, type IntlShape, type MessageDescriptor } from 'react-intl';

type FallbackValues = Record<string, unknown>;

// No-provider stand-in (Storybook, tests): there are no translations, so render the
// English defaultMessage, filling its `{value}` slots: 'Page {current} of {total}' with
// { current: 1, total: 2 } becomes 'Page 1 of 2'. Plurals (`{count, plural, ...}`) aren't evaluated.
const SIMPLE_ARGUMENT = /\{\s*([A-Za-z_][\w-]*)\s*\}/g;

// A value that isn't a string or number keeps its `{value}` slot, so the gap shows instead of "undefined".
const fillSimpleArguments = (message: string, values?: FallbackValues): string => {
  if (!values) return message;
  return message.replace(SIMPLE_ARGUMENT, (placeholder, name: string) => {
    const value = values[name];
    return typeof value === 'string' || typeof value === 'number'
      ? String(value)
      : placeholder;
  });
};

const fallbackFormatMessage = (
  descriptor: MessageDescriptor,
  values?: FallbackValues
): string => {
  const { defaultMessage, id } = descriptor;
  if (typeof defaultMessage === 'string')
    return fillSimpleArguments(defaultMessage, values);
  if (Array.isArray(defaultMessage)) {
    return defaultMessage.map((part) => (typeof part === 'string' ? part : '')).join('');
  }
  return typeof id === 'string' ? id : '';
};

const fallbackIntl = {
  formatMessage: fallbackFormatMessage,
} as unknown as IntlShape;

// Some consumer tests partially mock `react-intl` (e.g. only export `useIntl`),
// which leaves `IntlContext` undefined at module load. Substitute a no-op
// context so `useContext` never throws.
const SafeIntlContext = IntlContext ?? createContext<IntlShape | null>(null);

/**
 * Reads the ambient IntlShape, falling back to a minimal `en`-default intl
 * when no `<IntlProvider>` is present. Design-system primitives use this so
 * consumer unit tests don't break when a DS component adopts react-intl.
 *
 * The fallback implements only `formatMessage`, so DS primitives must use it
 * with a `defaultMessage`; `FormattedMessage` and `useIntl` throw without a
 * provider.
 */
export const useDesignSystemIntl = (): IntlShape =>
  useContext(SafeIntlContext) ?? fallbackIntl;
